import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  createdAt: string;
  bannedUntil: string | null;
  banReason: string | null;
  profile: {
    age: number;
    location: string;
    variant: {
      name: string;
    };
  } | null;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banHours, setBanHours] = useState('24');
  const [banReason, setBanReason] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // This endpoint needs to be created or we can use profile endpoint for now
      const response = await axios.get(`${API_URL}/users/browse`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setLoading(false);
    }
  };

  const banUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/moderation/admin/ban/${selectedUser.id}`,
        {
          hours: parseInt(banHours),
          reason: banReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('User banned successfully');
      setShowBanModal(false);
      fetchUsers();
    } catch (err: any) {
      alert('Failed to ban user: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const unbanUser = async (userId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/moderation/admin/unban/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('User unbanned successfully');
      fetchUsers();
    } catch (err: any) {
      alert('Failed to unban user: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Total Users: {users.length}</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">ID</th>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">Name</th>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">Email</th>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">Role</th>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">Status</th>
                <th className="text-left py-4 px-6 text-gray-700 font-semibold">Joined</th>
                <th className="text-right py-4 px-6 text-gray-700 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isBanned = user.bannedUntil && new Date(user.bannedUntil) > new Date();

                return (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6 text-gray-800 font-mono">{user.id}</td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-gray-900">{user.name}</div>
                      {user.profile && (
                        <div className="text-xs text-gray-500">
                          {user.profile.age}y • {user.profile.location}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6 text-gray-600">{user.email}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'moderator' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {isBanned ? (
                        <div>
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                            BANNED
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            Until: {new Date(user.bannedUntil!).toLocaleDateString()}
                          </div>
                          {user.banReason && (
                            <div className="text-xs text-gray-500">
                              Reason: {user.banReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                          ACTIVE
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-gray-600 text-sm">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6 text-right">
                      {isBanned ? (
                        <button
                          onClick={() => unbanUser(user.id)}
                          className="bg-green-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-700"
                        >
                          Unban
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowBanModal(true);
                          }}
                          className="bg-red-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-700"
                        >
                          Ban
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ban Modal */}
      {showBanModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ban User</h2>
            <p className="text-gray-600 mb-6">
              Ban {selectedUser.name} ({selectedUser.email})?
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (hours)
                </label>
                <input
                  type="number"
                  value={banHours}
                  onChange={(e) => setBanHours(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason *
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={3}
                  placeholder="Why is this user being banned?"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBanModal(false);
                  setBanReason('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={banUser}
                disabled={!banReason.trim()}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
